import type { QueryClient } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSubgraphConfig, SubgraphType } from 'config/subgraph'
import { fetchBlockMap } from 'hooks/backend/blocks'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { capBlocksToLatestIndexedBlock } from 'hooks/useSubgraphLatestIndexedBlock'
import { useEffect, useMemo, useState } from 'react'
import { UnifiedPoolData } from 'types/analytics'
import { buildSelectionKey, dedupeIds, toNumber } from 'utils/analytics'
import { calculateAPRPercent } from 'utils/analytics/calculateAPR'
import { get2DayChange, getDelta, getPercentChange } from 'utils/analytics/data'
import { useDeltaTimestamps } from 'utils/queries'

import type {
  GetV3PoolsByIdsQuery,
  GetV3PoolsByIdsQueryVariables,
  GetV3PoolsByTokenQuery,
  GetV3PoolsByTokenQueryVariables,
  GetV3TopPoolsQuery,
  GetV3TopPoolsQueryVariables,
} from '../../queries/analytics/v3/generated'
import {
  useGetV3PoolsByIdsQuery,
  useGetV3PoolsByTokenQuery,
  useGetV3PoolsSnapshotsForTableQuery,
  useGetV3PoolsSnapshotsQuery,
  useGetV3TopPoolsQuery,
} from '../../queries/analytics/v3/generated'

type V3PoolToken = Pick<V3Token, 'id' | 'symbol' | 'name' | 'decimals' | 'derivedETH'>

export type V3PoolData = Pick<
  V3Pool,
  | 'id'
  | 'feeTier'
  | 'liquidity'
  | 'sqrtPrice'
  | 'tick'
  | 'token0Price'
  | 'token1Price'
  | 'volumeUSD'
  | 'volumeToken0'
  | 'volumeToken1'
  | 'feesUSD'
  | 'txCount'
  | 'totalValueLockedToken0'
  | 'totalValueLockedToken1'
  | 'totalValueLockedUSD'
> & {
  token0: V3PoolToken
  token1: V3PoolToken
}

interface PoolMetrics
  extends Pick<
    UnifiedPoolData,
    | 'volumeUSD'
    | 'volumeUSDChange'
    | 'volumeUSDWeek'
    | 'feesUSD'
    | 'feesUSDWeek'
    | 'tvlUSDChange'
    | 'feesUSDChange'
    | 'APR'
  > {
  metricsLoaded: boolean
  metrics24hLoaded: boolean
  metrics7dLoaded: boolean
}

type FetchStrategy = { type: 'top' } | { type: 'byIds'; poolIds: string[] } | { type: 'byToken'; tokenAddress: string }

// Query Key Constants
const v3PoolsBaseQueryKey = (chainId: number, selectionKey: string) =>
  ['analytics', 'v3Pools', chainId, 'base', selectionKey] as const

export const v3PoolsMetricsQueryKey = (
  chainId: number,
  selectionKey: string,
  t24: number,
  t48: number,
  tWeek: number,
  include48hSnapshot: boolean,
  latestIndexedBlockNumber?: number
) =>
  [
    'analytics',
    'v3Pools',
    chainId,
    'metrics',
    selectionKey,
    t24,
    t48,
    tWeek,
    include48hSnapshot ? 'with48h' : 'no48h',
    latestIndexedBlockNumber ?? 'latest',
  ] as const

export const v3PoolsQueryKey = (
  chainId: number,
  selectionKey: string,
  t24: number,
  t48: number,
  tWeek: number,
  include48hSnapshot: boolean
) =>
  [
    'analytics',
    'v3Pools',
    chainId,
    'full',
    selectionKey,
    t24,
    t48,
    tWeek,
    include48hSnapshot ? 'with48h' : 'no48h',
  ] as const

type GraphV3Pool =
  | GetV3TopPoolsQuery['pools'][number]
  | GetV3PoolsByIdsQuery['pools'][number]
  | GetV3PoolsByTokenQuery['as0'][number]
  | GetV3PoolsByTokenQuery['as1'][number]

const mapGraphPoolToPoolData = (pool: GraphV3Pool): V3PoolData => ({
  id: pool.id,
  feeTier: pool.feeTier ?? '0',
  liquidity: pool.liquidity ?? '0',
  sqrtPrice: pool.sqrtPrice ?? '0',
  tick: pool.tick ?? '0',
  token0Price: pool.token0Price ?? '0',
  token1Price: pool.token1Price ?? '0',
  volumeUSD: pool.volumeUSD ?? '0',
  volumeToken0: pool.volumeToken0 ?? '0',
  volumeToken1: pool.volumeToken1 ?? '0',
  feesUSD: pool.feesUSD ?? '0',
  txCount: pool.txCount ?? '0',
  totalValueLockedToken0: pool.totalValueLockedToken0 ?? '0',
  totalValueLockedToken1: pool.totalValueLockedToken1 ?? '0',
  totalValueLockedUSD: pool.totalValueLockedUSD ?? '0',
  token0: {
    id: pool.token0.id,
    symbol: pool.token0.symbol,
    name: pool.token0.name,
    decimals: pool.token0.decimals,
    derivedETH: pool.token0.derivedETH ?? '0',
  },
  token1: {
    id: pool.token1.id,
    symbol: pool.token1.symbol,
    name: pool.token1.name,
    decimals: pool.token1.decimals,
    derivedETH: pool.token1.derivedETH ?? '0',
  },
})

// Utility Functions
const mapPoolsById = (pools?: V3PoolData[]) => {
  if (!pools) return {}
  return pools.reduce<Record<string, V3PoolData>>((acc, pool) => {
    acc[pool.id.toLowerCase()] = pool
    return acc
  }, {})
}

export const dedupeV3Ids = (ids?: string[]) => dedupeIds(ids)

export const buildV3PoolSelectionKey = (normalizedPoolIds?: string[], tokenAddress?: string) => {
  if (tokenAddress) return `token-${tokenAddress.toLowerCase()}`
  return buildSelectionKey(normalizedPoolIds)
}

const computePoolMetrics = (
  current: V3PoolData | undefined,
  oneDay: V3PoolData | undefined,
  twoDays: V3PoolData | undefined,
  week: V3PoolData | undefined,
  resolved: { day: boolean; week: boolean },
  previous?: PoolMetrics
): PoolMetrics => {
  const [volumeUSDRaw, volumeUSDChangeRaw] =
    current && oneDay && twoDays
      ? get2DayChange(current.volumeUSD, oneDay.volumeUSD, twoDays.volumeUSD)
      : current && oneDay
      ? [getDelta(current.volumeUSD, oneDay.volumeUSD), 0]
      : [0, 0]

  const volumeUSD = Number.isFinite(volumeUSDRaw) ? Math.max(volumeUSDRaw, 0) : 0
  const volumeUSDChange = Number.isFinite(volumeUSDChangeRaw) ? volumeUSDChangeRaw : 0
  const hasWeeklySnapshot = resolved.week && week !== undefined
  const volumeUSDWeek =
    hasWeeklySnapshot && current ? getDelta(current.volumeUSD, week.volumeUSD) : previous?.volumeUSDWeek ?? 0
  const feesUSD = current && oneDay ? getDelta(current.feesUSD, oneDay.feesUSD) : 0
  const feesUSDWeek =
    hasWeeklySnapshot && current ? getDelta(current.feesUSD, week.feesUSD) : previous?.feesUSDWeek ?? 0
  const tvlUSDChange = getPercentChange(current?.totalValueLockedUSD, oneDay?.totalValueLockedUSD)
  const feesUSDChange = getPercentChange(current?.feesUSD, oneDay?.feesUSD)

  const APR = calculateAPRPercent(feesUSD, Number(current?.totalValueLockedUSD))

  const metrics24hLoaded = resolved.day
  const metrics7dLoaded = (resolved.week && Boolean(current)) || previous?.metrics7dLoaded || false

  return {
    volumeUSD,
    volumeUSDWeek,
    feesUSD,
    feesUSDWeek,
    tvlUSDChange,
    volumeUSDChange,
    feesUSDChange,
    metricsLoaded: metrics24hLoaded && metrics7dLoaded,
    metrics24hLoaded,
    metrics7dLoaded,
    APR,
  }
}

const createPoolData = (pool: V3PoolData, overrides?: Partial<UnifiedPoolData>): UnifiedPoolData => {
  const base: UnifiedPoolData = {
    id: pool.id,
    address: pool.id,
    feeTier: Number(pool.feeTier),
    token0: {
      id: pool.token0.id,
      address: pool.token0.id,
      symbol: pool.token0.symbol,
      name: pool.token0.name,
      decimals: Number(pool.token0.decimals),
      derivedETH: Number(pool.token0.derivedETH),
    },
    token1: {
      id: pool.token1.id,
      address: pool.token1.id,
      symbol: pool.token1.symbol,
      name: pool.token1.name,
      decimals: Number(pool.token1.decimals),
      derivedETH: Number(pool.token1.derivedETH),
    },
    tvlUSD: parseFloat(pool.totalValueLockedUSD ?? '0'),
    volumeUSD: parseFloat(pool.volumeUSD),
    feesUSD: 0,
    volumeUSDWeek: 0,
    feesUSDWeek: 0,
    liquidity: toNumber(pool.liquidity),
    sqrtPrice: toNumber(pool.sqrtPrice),
    tick: toNumber(pool.tick),
    token0Price: toNumber(pool.token0Price),
    token1Price: toNumber(pool.token1Price),
    tvlToken0: toNumber(pool.totalValueLockedToken0),
    tvlToken1: toNumber(pool.totalValueLockedToken1),
    metricsLoaded: false,
    metrics24hLoaded: false,
    metrics7dLoaded: false,
  }

  return overrides ? { ...base, ...overrides } : base
}

const fetchPoolsByStrategy = async (
  endpoint: string,
  strategy: FetchStrategy,
  blockNumber?: number
): Promise<V3PoolData[]> => {
  const blockInput = blockNumber !== undefined ? { number: blockNumber } : undefined

  switch (strategy.type) {
    case 'top': {
      const variables: GetV3TopPoolsQueryVariables | undefined = blockInput ? { block: blockInput } : undefined
      const response = await useGetV3TopPoolsQuery.fetcher({ endpoint }, variables)()
      return (response.pools ?? []).map(mapGraphPoolToPoolData)
    }
    case 'byIds': {
      if (strategy.poolIds.length === 0) return []
      const variables: GetV3PoolsByIdsQueryVariables = {
        ids: strategy.poolIds,
        ...(blockInput ? { block: blockInput } : {}),
      }
      const response = await useGetV3PoolsByIdsQuery.fetcher({ endpoint }, variables)()
      return (response.pools ?? []).map(mapGraphPoolToPoolData)
    }
    case 'byToken': {
      const variables: GetV3PoolsByTokenQueryVariables = {
        tokens: [strategy.tokenAddress],
        ...(blockInput ? { block: blockInput } : {}),
      }
      const response = await useGetV3PoolsByTokenQuery.fetcher(
        { endpoint },

        variables
      )()
      const pools = [...(response.as0 ?? []), ...(response.as1 ?? [])]
      return pools.map(mapGraphPoolToPoolData)
    }
  }
}

type FetchPoolMetricsOptions = {
  providedBlocks?: Map<number, number>
  previousMetrics?: Record<string, PoolMetrics>
  include48hSnapshot?: boolean
  latestIndexedBlockNumber?: number
}

type SharedMetricsOptions = {
  blocksRequired?: boolean
  blocks?: Map<number, number>
  timestamps?: [number, number, number]
  include48hSnapshot?: boolean
  latestIndexedBlockNumber?: number
}

const fetchPoolMetrics = async (
  chainId: number,
  endpoint: string,
  currentPools: V3PoolData[],
  strategy: FetchStrategy,
  t24: number,
  t48: number,
  tWeek: number,
  options?: FetchPoolMetricsOptions
): Promise<Record<string, PoolMetrics>> => {
  if (!currentPools.length) return {}

  const { providedBlocks, previousMetrics, include48hSnapshot = true, latestIndexedBlockNumber } = options ?? {}

  const timestampsForBlocks = include48hSnapshot ? [t24, t48, tWeek] : [t24, tWeek]
  const uncappedBlocks = providedBlocks ?? (await fetchBlockMap({ chainId, timestamps: timestampsForBlocks }))
  const blocks = capBlocksToLatestIndexedBlock(uncappedBlocks, latestIndexedBlockNumber)
  const block24 = blocks.get(t24)
  const block48 = include48hSnapshot ? blocks.get(t48) : undefined
  const blockWeek = blocks.get(tWeek)

  const poolIdsForMetrics = Array.from(
    currentPools.reduce<Set<string>>((acc, pool) => {
      if (pool.id) {
        acc.add(pool.id)
      }
      return acc
    }, new Set<string>())
  )

  let pools24: V3PoolData[] = []
  let pools48: V3PoolData[] = []
  let poolsWeek: V3PoolData[] | undefined

  if (poolIdsForMetrics.length > 0) {
    if (include48hSnapshot) {
      const variables = {
        ids: poolIdsForMetrics,
        block24: block24 ? { number: block24 } : undefined,
        block48: block48 ? { number: block48 } : undefined,
        blockWeek: blockWeek ? { number: blockWeek } : undefined,
      }

      const snapshots = await useGetV3PoolsSnapshotsQuery.fetcher({ endpoint }, variables)()

      pools24 = (snapshots.block24 ?? []).map(mapGraphPoolToPoolData)
      pools48 = (snapshots.block48 ?? []).map(mapGraphPoolToPoolData)
      poolsWeek = snapshots.blockWeek ? snapshots.blockWeek.map(mapGraphPoolToPoolData) : undefined
    } else {
      const variables = {
        ids: poolIdsForMetrics,
        block24: block24 ? { number: block24 } : undefined,
        blockWeek: blockWeek ? { number: blockWeek } : undefined,
      }

      const snapshots = await useGetV3PoolsSnapshotsForTableQuery.fetcher({ endpoint }, variables)()

      pools24 = (snapshots.block24 ?? []).map(mapGraphPoolToPoolData)
      pools48 = []
      poolsWeek = snapshots.blockWeek ? snapshots.blockWeek.map(mapGraphPoolToPoolData) : undefined
    }
  }

  const currentById = mapPoolsById(currentPools)
  const dayById = mapPoolsById(pools24)
  const twoDaysById = mapPoolsById(pools48)
  const dayResolved = true

  const computeMetrics = (
    weekById?: Record<string, V3PoolData>,
    weekResolved = false,
    previousOverrides?: Record<string, PoolMetrics>
  ) =>
    currentPools.reduce<Record<string, PoolMetrics>>((acc, pool) => {
      const poolId = pool.id.toLowerCase()
      const previous = previousOverrides?.[poolId] ?? previousMetrics?.[poolId]
      acc[poolId] = computePoolMetrics(
        currentById[poolId],
        dayById[poolId],
        twoDaysById[poolId],
        weekById?.[poolId],
        {
          day: dayResolved,
          week: weekResolved,
        },
        previous
      )
      return acc
    }, {})

  const weekById = poolsWeek ? mapPoolsById(poolsWeek) : undefined
  const metrics = computeMetrics(weekById, Boolean(weekById), previousMetrics)

  return metrics
}

export const prefetchV3TopPools = async (
  queryClient: QueryClient,
  chainId: number,
  t24: number,
  t48: number,
  tWeek: number,
  blocks?: Map<number, number>,
  include48hSnapshot = true,
  latestIndexedBlockNumber?: number
) => {
  let endpoint: string
  try {
    endpoint = getSubgraphConfig(chainId, SubgraphType.V3).endpoint
  } catch (error) {
    console.error(error)
    return
  }

  const selectionKey = buildV3PoolSelectionKey(undefined)
  const strategy: FetchStrategy = { type: 'top' }

  const basePools =
    (await queryClient.ensureQueryData({
      queryKey: v3PoolsBaseQueryKey(chainId, selectionKey),
      queryFn: () => fetchPoolsByStrategy(endpoint, strategy),
      staleTime: 1000 * 60 * 5, // 5 minutes
    })) ?? []

  await queryClient.prefetchQuery({
    queryKey: v3PoolsMetricsQueryKey(
      chainId,
      selectionKey,
      t24,
      t48,
      tWeek,
      include48hSnapshot,
      latestIndexedBlockNumber
    ),
    queryFn: () =>
      fetchPoolMetrics(chainId, endpoint, basePools, strategy, t24, t48, tWeek, {
        providedBlocks: blocks,
        include48hSnapshot,
        latestIndexedBlockNumber,
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Unified Hook Core Logic
const useV3PoolsCore = (
  strategy: FetchStrategy,
  selectionKey: string,
  enabled: boolean,
  shared?: SharedMetricsOptions
) => {
  const defaultTimestamps = useDeltaTimestamps()
  const [t24, t48, tWeek] = shared?.timestamps ?? defaultTimestamps
  const providedBlocks = shared?.blocks
  const include48hSnapshot = shared?.include48hSnapshot ?? true
  const latestIndexedBlockNumber = shared?.latestIndexedBlockNumber
  const { chainId } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)
  const endpoint = subgraphConfig.endpoint
  const queryClient = useQueryClient()
  const [hasRefetchedEmptyBase, setHasRefetchedEmptyBase] = useState(false)

  const poolIds = strategy.type === 'byIds' ? strategy.poolIds : []
  const tokenAddress = strategy.type === 'byToken' ? strategy.tokenAddress : ''
  const baseQueryKey = useMemo(() => v3PoolsBaseQueryKey(chainId, selectionKey), [chainId, selectionKey])

  const topPoolsQuery = useGetV3TopPoolsQuery<GetV3TopPoolsQuery>(
    {
      endpoint,
    },
    undefined,
    {
      enabled: enabled && strategy.type === 'top' && Boolean(subgraphConfig),
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3TopPoolsQuery) => response,
    }
  )

  const poolsByIdsQuery = useGetV3PoolsByIdsQuery<GetV3PoolsByIdsQuery>(
    {
      endpoint,
    },
    {
      ids: poolIds,
    },
    {
      enabled: enabled && strategy.type === 'byIds' && poolIds.length > 0 && Boolean(subgraphConfig),
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3PoolsByIdsQuery) => response,
    }
  )

  const poolsByTokenQuery = useGetV3PoolsByTokenQuery<GetV3PoolsByTokenQuery>(
    {
      endpoint,
    },
    {
      tokens: [tokenAddress],
    },
    {
      enabled: enabled && strategy.type === 'byToken' && Boolean(subgraphConfig),
      queryKey: baseQueryKey,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3PoolsByTokenQuery) => response,
    }
  )

  const rawTopPools = useMemo(() => topPoolsQuery.data?.pools ?? [], [topPoolsQuery.data?.pools])
  const rawPoolsByIds = useMemo(() => poolsByIdsQuery.data?.pools ?? [], [poolsByIdsQuery.data?.pools])
  const rawPoolsByToken = useMemo(() => {
    const as0 = poolsByTokenQuery.data?.as0 ?? []
    const as1 = poolsByTokenQuery.data?.as1 ?? []
    if (!as0.length && !as1.length) return [] as GraphV3Pool[]
    return [...as0, ...as1]
  }, [poolsByTokenQuery.data?.as0, poolsByTokenQuery.data?.as1])

  const baseQuery =
    strategy.type === 'top' ? topPoolsQuery : strategy.type === 'byIds' ? poolsByIdsQuery : poolsByTokenQuery

  const basePoolNodes = useMemo(() => {
    switch (strategy.type) {
      case 'top':
        return topPoolsQuery.data ? rawTopPools : undefined
      case 'byIds':
        return poolsByIdsQuery.data ? rawPoolsByIds : undefined
      case 'byToken':
        return poolsByTokenQuery.data ? rawPoolsByToken : undefined
    }
  }, [
    strategy.type,
    rawTopPools,
    rawPoolsByIds,
    rawPoolsByToken,
    topPoolsQuery.data,
    poolsByIdsQuery.data,
    poolsByTokenQuery.data,
  ])

  const baseData = useMemo(
    () => (basePoolNodes ? basePoolNodes.map(mapGraphPoolToPoolData) : undefined),
    [basePoolNodes]
  )

  const baseDataLength = baseData?.length ?? 0
  const deferEmptyResult = (!baseData && baseQuery.isFetching) || (baseDataLength === 0 && baseQuery.isFetching)

  useEffect(() => {
    if (!enabled) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (!baseQuery.isFetched || baseQuery.isFetching) return

    if (baseDataLength > 0) {
      if (hasRefetchedEmptyBase) {
        setHasRefetchedEmptyBase(false)
      }
      return
    }

    if (hasRefetchedEmptyBase) return

    setHasRefetchedEmptyBase(true)
    void baseQuery.refetch()
  }, [enabled, baseDataLength, baseQuery, hasRefetchedEmptyBase])

  useEffect(() => {
    setHasRefetchedEmptyBase(false)
  }, [selectionKey])

  const hasBaseData = baseDataLength > 0

  const metricsQueryKey = useMemo(
    () => v3PoolsMetricsQueryKey(chainId, selectionKey, t24, t48, tWeek, include48hSnapshot, latestIndexedBlockNumber),
    [chainId, selectionKey, t24, t48, tWeek, include48hSnapshot, latestIndexedBlockNumber]
  )

  const metricsQuery = useQuery({
    queryKey: metricsQueryKey,
    queryFn: () => {
      const previousMetrics = queryClient.getQueryData<Record<string, PoolMetrics>>(metricsQueryKey)
      return fetchPoolMetrics(chainId, endpoint, baseData ?? [], strategy, t24, t48, tWeek, {
        providedBlocks,
        previousMetrics,
        include48hSnapshot,
        latestIndexedBlockNumber,
      })
    },
    enabled: enabled && hasBaseData && (shared?.blocksRequired ?? true),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const metricsData = metricsQuery.data

  const basePools = useMemo(() => {
    if (deferEmptyResult || !baseData) return undefined
    return baseData.map((pool) => createPoolData(pool))
  }, [baseData, deferEmptyResult])

  const combinedData = useMemo(() => {
    if (!basePools || !metricsData) return undefined
    const metricsById = metricsData

    return basePools.map((pool) => {
      const poolId = pool.id.toLowerCase()
      const metrics = metricsById[poolId]
      if (!metrics) return pool

      return { ...pool, ...metrics }
    })
  }, [basePools, metricsData])

  const refetch = async () => {
    const baseResult = await baseQuery.refetch({ cancelRefetch: false })
    if (!metricsQuery.isPaused) {
      await metricsQuery.refetch({ cancelRefetch: false })
    }
    return baseResult
  }

  const hasCombinedData = combinedData !== undefined

  return {
    data: combinedData,
    isLoading: (baseQuery.isLoading || metricsQuery.isLoading) && !hasCombinedData,
    isFetching: baseQuery.isFetching || metricsQuery.isFetching,
    error: baseQuery.error ?? metricsQuery.error,
    refetch,
  }
}

// Public Hooks
type UseV3PoolDataOptions = SharedMetricsOptions & {
  enabled?: boolean
}

export const useV3PoolDatas = (poolIds?: string[], options?: UseV3PoolDataOptions) => {
  const normalizedPoolIds = useMemo(() => dedupeV3Ids(poolIds), [poolIds])
  const selectionKey = useMemo(() => buildV3PoolSelectionKey(normalizedPoolIds), [normalizedPoolIds])

  const strategy: FetchStrategy = useMemo(
    () => (normalizedPoolIds ? { type: 'byIds', poolIds: normalizedPoolIds } : { type: 'top' }),
    [normalizedPoolIds]
  )

  const enabled = (options?.enabled ?? true) && (normalizedPoolIds === undefined || normalizedPoolIds.length > 0)

  return useV3PoolsCore(strategy, selectionKey, enabled, options)
}

export const useV3PoolsByToken = (address: string, options?: UseV3PoolDataOptions) => {
  const selectionKey = buildV3PoolSelectionKey(undefined, address)
  const strategy: FetchStrategy = { type: 'byToken', tokenAddress: address }

  return useV3PoolsCore(strategy, selectionKey, options?.enabled ?? true, options)
}
